import { Flex, Space } from "antd"
import cx from "clsx"
import { Card, DataTable, Icon, Skeleton, Text, Tooltip } from "components/common"
import { TreeDropdown } from "components/common/hierarchicalTreeDropdown/treeDropdown"
import { Separator } from "components/custom"
import { getAppLanguage } from "configs/useInitializeApp"
import UserProfileSummary from "features/shared/components/userProfileSummary"
import { t } from "i18next"
import { useCallback, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { setExpandedColumnsState } from "store/reportsSlice"
import { joinBytItems, localisedValue } from "utils"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES } from "utils/gtm/gtmEventsData"
import theme from "utils/themeVars"
import ReportTablePopOver from "../reportTablePopOver/reportTablePopOver"
import styles from "./index.module.less"

const PERCENTAGE_COLORS = [
  { threshold: 85, color: "#BBE2CA" },
  { threshold: 70, color: "#DDF1E5" },
  { threshold: 55, color: "#EFF5E6" },
  { threshold: 40, color: "#FDF7E3" },
  { threshold: 25, color: "#F7E6D7" },
  { threshold: 0, color: "#F9D3CA" },
]

export default function ReportsTable(props) {
  const {
    preSelectedColumns,
    targetQueryObj,
    data,
    error,
    refetch,
    isFetching,
    loadType = "userKpi",
    dataExtractFunc = e => `${e.parent}.${e.key}`,
    from,
    canFilterColumns = true,
    withStaffInfo = true,
    handleRowClick = () => {},
    rowClickAble = false,
    onCellClick = () => {},
    queryObj,
    extra = () => {},
    showCard = true,
    extraColumns,
    extraColumnData,
    stickyOffset = 142,
    renderHeaderCustomLeft = null,
    renderHeaderCustomRight = null,
    reportCategory = null, //use in case we have two kpi tables in one report
    enableColumnExpansion = true, // If true, show expand/collapse icons and hide columns. If false, show all columns without icons.
    showbackgroundColorForPercentage = false, // If true, show background color for percentage columns. If false, don't show background color for percentage columns.
  } = props

  const navigate = useNavigate()
  const selectedColumns = preSelectedColumns || []
  const [selectedTableRowId, setSelectedTableRowId] = useState([])
  const selectedSideMenuItem = useSelector(state => state.app.selectedSideMenuItem)?.key || "staffList"
  const reportName = reportCategory || selectedSideMenuItem

  let expandedColumns = useSelector(state => state.reports.reports[reportName]?.expandedColumns || {})

  const dispatch = useDispatch()
  const toggleColumnExpansion = (columnKey = "staffList") => {
    dispatch(
      setExpandedColumnsState({
        name: reportName,
        expandedColumns: {
          ...expandedColumns,
          [columnKey]: !expandedColumns[columnKey],
        },
      })
    )
  }

  const getSelectedChildren = (children, selectedKeys, hideKey) => {
    let totalColumns = [...children]
    let targetOnlyColumns = []
    let selectedColumns = []

    if (hideKey) {
      targetOnlyColumns = totalColumns.filter(child => !!child.target)
    }

    if (selectedKeys?.length) {
      selectedColumns = children.filter(child => selectedKeys.find(key => key == child.dataIndex))
    }
    const union = [...new Map([...targetOnlyColumns, ...selectedColumns].map(obj => [obj?.dataIndex, obj])).values()]

    return hideKey || selectedKeys?.length ? union : children
  }

  const getPercentageColor = value => {
    const match = PERCENTAGE_COLORS.find(({ threshold }) => value >= threshold)
    return match ? match.color : PERCENTAGE_COLORS[PERCENTAGE_COLORS.length - 1].color
  }

  const columns = useMemo(() => {
    let tableColumns = [
      {
        title: data?.userKpi?.data?.[0]?.type === t("region") ? t("Region/City") : t("Staff") || "",
        dataIndex: "name",
        key: "name",
        fixed: "left",
        width: 300,
        onCell: record => {
          return {
            style: record?.percentage ? { height: "72px", alignItems: "center" } : {},
          }
        },
      },
      ...(data?.listKeys
        ? data?.listKeys.map(item => ({
            ...item,
            title:
              item && item?.children?.length > 1 && enableColumnExpansion ? (
                <Space
                  size={4}
                  onClick={() => {
                    toggleColumnExpansion(item.key)
                  }}
                  className="pointer"
                >
                  {item.label}
                  <Icon
                    size="1.1em"
                    color={theme["accent-color-alt"]}
                    icon={expandedColumns[item.key] ? "BiSolidMinusSquare" : "BiSolidPlusSquare"}
                    style={{
                      marginTop: 4,
                    }}
                  />
                </Space>
              ) : (
                item?.label
              ),
            onCell: (_, index) => {
              return { className: "border-left" }
            },
            children: getSelectedChildren(
              item?.children?.map((e, i) => ({
                title: (
                  <Tooltip
                    placement={"topLeft"}
                    title={
                      e?.description?.length ? (
                        <div>
                          <strong>{`${e?.label}: `}</strong> {e?.description}
                        </div>
                      ) : (
                        e?.label
                      )
                    }
                    key={i}
                  >
                    <span>{e?.short}</span>
                  </Tooltip>
                ),
                dataIndex: e.key,
                slopeKey: e?.slopeKey,
                avgKpi: e?.averageKey,
                isLinkable: e?.linkable,
                hasDrawer: e?.hasDrawer,
                parentIndex: e?.parent,
                ...(targetQueryObj?.showTarget && { target: e?.target }),
                ...(targetQueryObj?.showTarget && { targetType: targetQueryObj?.showTarget || "percentage" }),
                url: e?.urlMapping?.url,
                userMappingKey: e?.urlMapping?.userMappingKey,
                dataKey: dataExtractFunc(e),
                sorter: (a, b) => {
                  let dataIdentifier = dataExtractFunc(e)?.split(".")
                  let parentKey = dataIdentifier?.[0]
                  let childKey = dataIdentifier?.[1]
                  if (e?.type === "ratio") {
                    const parseRatio = value => {
                      const [numerator, denominator] = value?.split(":")?.map(Number) || []
                      return numerator && denominator ? numerator / denominator : 0
                    }
                    const firstValue = parseRatio(a?.[parentKey]?.[childKey])
                    const secondValue = parseRatio(b?.[parentKey]?.[childKey])
                    return firstValue - secondValue
                  }
                  return a?.[parentKey]?.[childKey] - b?.[parentKey]?.[childKey]
                },
                showSorterTooltip: false,
                hoverKeys: e?.hoverKeys,
                component:
                  (e.type === "number" || e?.type === "time") && targetQueryObj?.showTarget
                    ? "target"
                    : e.comparity
                    ? [!!e.type ? e.type : "number", "comparity"]
                    : (e.type == "number" || e.type == "currency") && e.showPercentage === true
                    ? "showAddtionalValueAsPercentage"
                    : e.type || "number",
                align: "center",
                width: e.type === "time" && targetQueryObj?.showTarget ? 170 : e.type === "time" ? 150 : 100,
                label: e.label,
                type: (e.type === "number" || e?.type === "time") && targetQueryObj?.showTarget ? "target" : e.type,
                ...(targetQueryObj?.showTarget && { targetTypeValue: e?.type }),
                className: e?.hoverKeys && (Object.keys(e?.hoverKeys).length !== 0 ? "pointer" : null),
                from: from?.page,
                color: e?.colorCode,
              })),
              selectedColumns,
              targetQueryObj?.hideTargetColumns
            )
              .map((e, i) => ({
                ...e,
                onCell: (_, index) => {
                  const isLinkable = e?.isLinkable && !queryObj?.regionId && _?.[e?.parentIndex]?.[e?.dataIndex] > 0
                  const canOpenDrawer = e?.hasDrawer && e?.hasDrawer?.isActive && _?.[e?.parentIndex]?.[e?.dataIndex] > 0
                    

                  // code to add colors on table columns based on percentage value
                  // Determine background color based on percentage value
                  let backgroundColor = showbackgroundColorForPercentage
                    ? getPercentageColor(_?.["percentage"]?.[e?.parentIndex]?.[e?.dataIndex])
                    : null

                  return {
                    onClick: () => {
                      if (isLinkable) {
                        const url = `/${getAppLanguage()?.key}${e?.url}&${e.userMappingKey
                          ?.map(key => `${key}=${_?.userId}`)
                          ?.join("&")}${
                          queryObj?.staffType === "individual" ? `&${e?.userMappingKey}_withoutChildren=true` : ""
                        }`

                        window.open(url, "_blank")
                      } else if (canOpenDrawer) {
                        onCellClick(
                          {
                            type: e?.hasDrawer?.type,
                            parentKey: e?.parentIndex,
                            childKey: e?.dataIndex,
                            label: e?.label,
                          },
                          _
                        )
                      }
                    },
                    style: {
                      backgroundColor: backgroundColor,
                    },
                    ...(i === 0
                      ? {
                          className: isLinkable || canOpenDrawer ? cx("border-left", styles.cellLink) : "border-left",
                        }
                      : {
                          className: (isLinkable || canOpenDrawer) && styles.cellLink,
                        }),
                  }
                },
              }))
              ?.filter((_, i) => {
                return enableColumnExpansion ? expandedColumns[item.key] || i === 0 : true
              }),
          }))
        : []),
      ...(extraColumns?.length ? extraColumns : []),
    ]
    let filteredColumns = tableColumns.filter(col => col.children?.length != 0)

    return filteredColumns
  }, [data, selectedColumns, expandedColumns, targetQueryObj, enableColumnExpansion])

  const rosterDays = item => {
    switch (reportName) {
      case "kpi-sales":
      case "revenue-report":
      case "productivity-report":
        return item?.selfSeller ? item?.selfSellerActiveRosterDays : null

      case "kpi-affiliates":
        return item?.selfSeller ? item?.userActiveRosterDays : null

      case "kpi-telesales":
        return item?.isTelesales ? item?.userActiveRosterDays : null

      default:
        return item?.selfSeller || item?.isTelesales ? item?.userActiveRosterDays : null
    }
  }

  const getDataAgainstColumns = (item, index, level) => {
    let currentLevel = level + 1
    let itemIndex = index?.toString()
    const obj = {
      ...item,
      level: currentLevel,
      key: item.type === "region" ? itemIndex : item?.userId,
      name:
        item.type === "region" ? (
          item?.name?.length > 0 ? (
            localisedValue({ key: "name", object: item })
          ) : (
            "-"
          )
        ) : (
          <div>
            {localisedValue({ key: "name", object: item })}&nbsp;
            {withStaffInfo && <UserProfileSummary staffId={item?.userId} onClick={e => e?.stopPropagation()} />}
            <div className="gray-800 fs14">
              {joinBytItems(
                [
                  localisedValue({ key: "designation", object: item }),
                  localisedValue({ key: "reporteesCount", object: item }),
                  localisedValue({ object: "region", key: item }),
                  item?.joiningDate ? <Tooltip title={t("Days Since Joining")}>{item?.joiningDate}</Tooltip> : null,
                  rosterDays(item) != null ? (
                    <Tooltip title={t("Roster Days")}>{rosterDays(item) + " " + t(" Workdays")}</Tooltip>
                  ) : null,
                ].filter(Boolean),
                <Separator />
              )}{" "}
            </div>
          </div>
        ),
      ...(extraColumnData ? extraColumnData(item) : {}),
    }
    if (item.children) {
      obj.children = item.children.map((e, index) =>
        getDataAgainstColumns(e, (itemIndex + "-" + index).toString(), currentLevel)
      )
    }
    return obj
  }

  const getRowClassName = useCallback(
    (record, i) => {
      const isExpanded = selectedTableRowId?.find(e => e === record.id || e === record.parentId)
      return `${record?.children?.length > 0 ? styles.recordHasChild : ""} ${isExpanded ? "highlight" : ""}`
    },
    [selectedTableRowId]
  )

  const onRow = useCallback(
    record => {
      const isExpanded = selectedTableRowId?.find(e => e === record.id || e === record.parentId)
      return {
        style: isExpanded ? { "--record-bg-opacity": record.level > 1 ? `${100 - record.level * 6}%` : "initial" } : {},
        onClick: event => {
          handleRowClick(event, record)
        },
        className: rowClickAble && "pointer",
      }
    },
    [
      selectedTableRowId,
      // queryObj
    ]
  )

  const tableData = useMemo(() => {
    return data?.userKpi?.data?.map((e, index) => getDataAgainstColumns(e, index, 0))
  }, [data])

  const onApplyFilter = () => {
    fireEvent({ ...ACTION_TYPES.kpi_metrics_selection, ...from })
  }
  const getColCount = useMemo(() => {
    let count = 0
    const countRecursive = item => {
      if (item.children && item.children.length > 0) {
        item.children.forEach(countRecursive)
      } else {
        count += 1
      }
    }
    columns.forEach(countRecursive)
    return count
  }, [columns])
  const tableContent = () => (
    <DataTable
      className={styles.tableKpi}
      tableClass={styles.tableReports}
      error={error}
      onErrorRetry={refetch}
      scroll={getColCount > 7 && { x: 1800 }}
      columns={columns}
      data={tableData || []}
      loading={isFetching}
      rowClassName={getRowClassName}
      onRow={onRow}
      bordered
      stickyOffset={stickyOffset}
      shouldTransformData
      indentSize={28}
      showPagination
      defaultPageSize={50}
      skeletonRowCount={1}
      expandable={{
        rowExpandable: record => record?.children?.length > 0,
        onExpand: (isExpanded, record) => {
          record.level === 1 &&
            setSelectedTableRowId(prev => (!isExpanded ? prev.filter(e => e !== record.id) : [...prev, record.id]))
        },
        expandIcon: ({ expanded, onExpand, record }) =>
          record?.children?.length > 0 && (
            <Icon
              size="1.4em"
              color={theme["gray800"]}
              icon={
                getAppLanguage()?.key === "ar"
                  ? expanded
                    ? "HiChevronDown"
                    : "HiChevronLeft"
                  : expanded
                  ? "HiChevronDown"
                  : "HiChevronRight"
              }
              onClick={e => {
                onExpand(record, e)
                e?.stopPropagation()
              }}
              style={{ cursor: "pointer" }}
            />
          ),
      }}
    />
  )

  return showCard ? (
    <Card
      className="span-all"
      title={
        isFetching && renderHeaderCustomLeft ? (
          <Skeleton active paragraph={false} />
        ) : renderHeaderCustomLeft ? (
          <>
            <Flex vertical gap="6px">
              {t("Report")} {renderHeaderCustomLeft}
            </Flex>
          </>
        ) : data?.[loadType]?.toolTip ? (
          <div>
            {t("Report")} <ReportTablePopOver data={data[loadType].toolTip?.content} />
          </div>
        ) : (
          <>{t("Report")}</>
        )
      }
      bodyStyle={{ padding: 0 }}
      extra={
        renderHeaderCustomRight ? (
          isFetching ? (
            <Skeleton active paragraph={false} />
          ) : (
            <>
              <Space style={{ marginInline: "12px" }}>{extra()}</Space>
              <Flex vertical gap="12px">
                {renderHeaderCustomRight}
                {showbackgroundColorForPercentage && (
                  <Flex align="center" gap="12px" style={{ marginLeft: "auto" }}>
                    <Text type="secondary">{t("High")}</Text>
                    <Flex gap="16px">
                      {PERCENTAGE_COLORS.map(({ color }) => (
                        <div
                          key={color}
                          style={{
                            width: 30,
                            height: 20,
                            backgroundColor: color,
                            borderRadius: 2,
                          }}
                        />
                      ))}
                    </Flex>
                    <Text type="secondary">{t("Low")}</Text>
                  </Flex>
                )}
              </Flex>
            </>
          )
        ) : isFetching && canFilterColumns ? (
          <Skeleton active paragraph={false} />
        ) : (
          <>
            <Space style={{ marginInline: "12px" }}>{extra()}</Space>
            {canFilterColumns && (
              <Space>
                <Text strong>{t("Column Filters")} </Text>
                <TreeDropdown
                  disabled={isFetching || error}
                  data={data?.listKeys}
                  onApplyFilter={onApplyFilter}
                  labelKey={"label"}
                />
              </Space>
            )}
          </>
        )
      }
    >
      {tableContent({})}
    </Card>
  ) : (
    tableContent()
  )
}
